import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Send, Zap } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRequestConnection, useCreateDirectConnection } from "@/api/mutations/connections";
import type { Agent } from "@/types";

type Mode = "request" | "direct";

// #design-pass W10 (Graph): beim Verbinden zweier Agents. Request = Owner-Flow (pending →
// Accept). Direct = admin-only, sofort aktiv (Use-Case Service-Agents). Default = Request;
// Direct vorausgewählt, wenn ein Service-Agent (ohne Owner-Flow) beteiligt ist.
export function ConnectAgentsDialog({
  open,
  onOpenChange,
  source,
  target,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: Agent;
  target: Agent;
}) {
  const serviceInvolved = source.type === "service-agent" || target.type === "service-agent";
  const [mode, setMode] = useState<Mode>(serviceInvolved ? "direct" : "request");
  const [reason, setReason] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const requestM = useRequestConnection();
  const directM = useCreateDirectConnection();
  const isPending = requestM.isPending || directM.isPending;

  function handleSubmit() {
    setServerError(null);
    if (mode === "request") {
      requestM.mutate(
        { requesterAgentId: source.id, targetAgentId: target.id, reason: reason.trim() || null },
        {
          onSuccess: () => { toast.success("Connection-Request gesendet"); onOpenChange(false); },
          onError: (err) => { const m = (err as Error).message; setServerError(m); toast.error(`Request fehlgeschlagen: ${m}`); },
        },
      );
    } else {
      directM.mutate(
        { agentAId: source.id, agentBId: target.id },
        {
          onSuccess: () => { toast.success("Connection direkt erstellt"); onOpenChange(false); },
          onError: (err) => { const m = (err as Error).message; setServerError(m); toast.error(`Direct fehlgeschlagen: ${m}`); },
        },
      );
    }
  }

  const ModeRow = ({ value, icon: Icon, title, desc }: { value: Mode; icon: typeof Send; title: string; desc: string }) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      className={cn(
        "flex w-full items-start gap-3 rounded-md border px-3.5 py-3 text-left transition-colors",
        mode === value ? "border-accent-border bg-accent-soft" : "border-border hover:bg-surface-hover",
      )}
    >
      <span className={cn("mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full border", mode === value ? "border-accent" : "border-border-strong")}>
        {mode === value && <span className="h-2 w-2 rounded-full bg-accent" />}
      </span>
      <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", mode === value ? "text-accent" : "text-text3")} />
      <span className="min-w-0">
        <span className="block text-[13.5px] font-semibold text-text">{title}</span>
        <span className="block text-[12px] text-text3">{desc}</span>
      </span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="connect-agents-dialog">
        <DialogHeader>
          <DialogTitle>Agents verbinden</DialogTitle>
          <DialogDescription>A2A-Connection zwischen den beiden Agents anlegen.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2.5 rounded-md border border-border bg-surface2 px-3.5 py-2.5">
          <span className="mono truncate text-[13px] font-semibold text-text">{source.name}</span>
          <ArrowRight className="h-4 w-4 shrink-0 text-text3" />
          <span className="mono truncate text-[13px] font-semibold text-text">{target.name}</span>
        </div>

        <div className="space-y-2">
          <ModeRow value="request" icon={Send} title="Request senden" desc="Owner des Ziels bestätigt — erst dann aktiv (Standard)." />
          <ModeRow value="direct" icon={Zap} title="Direkt verbinden (Admin)" desc="Überspringt den Request-Flow, sofort aktiv. Für Service-Agents." />
        </div>

        {mode === "request" && (
          <div className="space-y-1">
            <Label htmlFor="connect-reason">Reason (optional, max 500)</Label>
            <Textarea
              id="connect-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              data-testid="connect-reason"
            />
          </div>
        )}

        {serverError && <div className="text-sm text-danger" data-testid="connect-error">{serverError}</div>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={isPending} data-testid="connect-submit">
            {isPending ? "..." : mode === "request" ? "Request senden" : "Direkt verbinden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
